/**
 * Search controller.
 *
 * Owns query/filter/pagination state and the search call itself, so the
 * standalone page and the embedded overlay share one implementation and cannot
 * drift in ranking, filter handling or paging behaviour.
 *
 * Keyword selection (docs/concepts/concept_curated_keywords.md §10.4) narrows
 * the result set of the current search on the client: the first page of a
 * search carries every result with its keyword IDs, and a page of the
 * narrowed set is loaded by document ID. The selection is kept when the query
 * or a filter changes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getSelectedLanguage } from '../components/LanguageSelector';
import { apiService, ApiServiceError } from '../services';
import { trackEvent } from '../services/googleAnalytics';
import { useAuth } from '../auth';
import { getFacetValues, useFacets } from './useFacets';
import { narrowedDocumentIds, toggleKeyword as toggled } from '../searchCore/keywords';
import { PHRASE_HINT_MIN_RESULTS, phraseHintQuery, syntaxDrift } from '../searchCore/querySyntax';
import type { SearchUrlState } from '../searchCore/urlState';
import type { KeywordNavigation, SearchFilters, SearchResponse } from '../types/search';

export const PAGE_SIZE = 20;

/** Offer of the exact phrase for a plain query (docs/concepts/concept_advanced_search_syntax.md §5.5). */
export interface PhraseHintState {
  /** The searched query the hint belongs to. */
  query: string;
  /** The phrase including its quotes. */
  phrase: string;
  count: number;
}

/** Why a search ran — separates user-typed searches from automatic re-runs in analytics. */
type SearchTrigger = 'new' | 'filter' | 'page' | 'language' | 'auth' | 'topic';

export function useSearchController() {
  const { accessToken } = useAuth();
  const { facets } = useFacets();

  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  // The last search actually run; unlike `query`/`filters` it ignores unsubmitted edits.
  const [activeSearch, setActiveSearch] = useState<SearchUrlState | null>(null);
  // Keyword navigation of the active search (null: feature off or no keyword list).
  const [navigation, setNavigation] = useState<KeywordNavigation | null>(null);
  const [keywords, setKeywords] = useState<string[]>([]);
  const [phraseHint, setPhraseHint] = useState<PhraseHintState | null>(null);

  // Keep refs so callbacks always read the latest values without re-binding.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const queryRef = useRef(query);
  queryRef.current = query;
  const keywordsRef = useRef(keywords);
  keywordsRef.current = keywords;
  const activeSearchRef = useRef(activeSearch);
  activeSearchRef.current = activeSearch;
  const navigationRef = useRef(navigation);
  navigationRef.current = navigation;
  // Unnarrowed first page of the active search; reused when the selection changes.
  const firstPageRef = useRef<SearchResponse | null>(null);
  // Last response shown; the base of narrowed pages when the first page isn't known.
  const lastResponseRef = useRef<SearchResponse | null>(null);
  // Only the latest load may update the state; earlier responses are dropped.
  const loadRef = useRef(0);

  useEffect(() => {
    apiService.setAccessToken(accessToken);
  }, [accessToken]);

  /** Remove filters where all values are selected (equivalent to no filter). */
  const optimizeFilters = useCallback(
    (searchFilters: SearchFilters): SearchFilters => {
      const optimized: SearchFilters = {};
      for (const [key, values] of Object.entries(searchFilters)) {
        if (!values || !Array.isArray(values) || values.length === 0) continue;
        // zenon_version is exempt: an absent version filter means "latest" on the
        // backend, not "all versions". Dropping a fully-selected version filter
        // would silently narrow the search to a single release.
        const facet =
          key === 'zenon_version' ? undefined : facets.find((f) => f.field_name === key);
        if (facet) {
          const allValues = getFacetValues(facet);
          if (values.length < allValues.length) {
            optimized[key] = values;
          }
        } else {
          optimized[key] = values;
        }
      }
      return optimized;
    },
    [facets]
  );

  /**
   * Run a search, or (trigger `page`/`topic`) show another page or selection
   * of the active search. A new search always fetches the first page, which
   * carries the keyword navigation; other pages are loaded from there.
   */
  const executeSearch = useCallback(
    async (
      searchQuery: string,
      searchFilters: SearchFilters,
      fromOffset: number,
      trigger: SearchTrigger,
      selection: string[]
    ) => {
      if (!searchQuery.trim()) return;

      const load = ++loadRef.current;
      const resultPage = Math.floor(fromOffset / PAGE_SIZE) + 1;
      setIsLoading(true);
      setError(null);
      setHasSearched(true);
      setActiveSearch({ query: searchQuery, filters: searchFilters, page: resultPage, keywords: selection });
      // Paging and narrowing keep the hint of the search they belong to.
      const freshResults = trigger !== 'page' && trigger !== 'topic';
      if (freshResults) setPhraseHint(null);

      const apiFilters = optimizeFilters(searchFilters);
      const searchTerm = searchQuery.trim();
      const request = {
        query: searchQuery,
        filters: apiFilters,
        size: PAGE_SIZE,
        use_semantic: true,
        user_language: getSelectedLanguage(),
      };

      try {
        let firstPage = firstPageRef.current;
        let nav = navigationRef.current;
        let resolved = selection;
        // Paging and narrowing reuse the active search: its navigation is enough
        // to narrow, its first page to show the whole set again.
        const sameSearch = (trigger === 'page' || trigger === 'topic') && (firstPage !== null || nav !== null);
        if (!sameSearch) {
          firstPage = await apiService.search({
            ...request,
            from: 0,
            selected_keyword_ids: selection.length > 0 ? selection : undefined,
          });
          nav = firstPage.keyword_navigation ?? null;
          // Without navigation data there is nothing to narrow.
          resolved = nav ? nav.selected : [];
          // The backend must read advanced queries like the frontend parser does.
          const normalized = firstPage.query_interpretation?.normalized_query;
          if (syntaxDrift(searchQuery, normalized)) {
            console.warn('Advanced search syntax drift', { query: searchQuery, backend: normalized });
            trackEvent('query_syntax_drift', { search_term: searchTerm, backend: normalized ?? '' });
          }
        }

        let response: SearchResponse;
        if (nav && resolved.length > 0) {
          const ids = narrowedDocumentIds(nav, resolved);
          const pageIds = ids.slice(fromOffset, fromOffset + PAGE_SIZE);
          const found = pageIds.length > 0 ? (await apiService.lookupDocuments(pageIds)).results : [];
          response = {
            ...(firstPage ?? lastResponseRef.current!),
            results: found,
            total: ids.length,
            from: fromOffset,
            size: found.length,
            took_ms: undefined,
          };
        } else if (fromOffset === 0) {
          // After restoring a narrowed view the unnarrowed first page isn't known yet.
          firstPage ??= await apiService.search({ ...request, from: 0 });
          response = firstPage;
        } else {
          response = await apiService.search({ ...request, from: fromOffset });
        }
        if (load !== loadRef.current) return;

        // The response keeps the navigation, so a restored snapshot can narrow again.
        response = { ...response, keyword_navigation: nav };
        firstPageRef.current = firstPage;
        lastResponseRef.current = response;
        setNavigation(nav);
        setKeywords(resolved);
        keywordsRef.current = resolved;
        if (resolved.join() !== selection.join()) {
          setActiveSearch({ query: searchQuery, filters: searchFilters, page: resultPage, keywords: resolved });
        }
        setResults(response);

        // Exact-phrase hint: counted after the results are shown, so it never delays them.
        const phrase = phraseHintQuery(searchQuery);
        const plainTotal = response.total;
        if (freshResults && phrase && resolved.length === 0 && plainTotal >= PHRASE_HINT_MIN_RESULTS) {
          apiService
            .search({ ...request, query: phrase, from: 0, count_only: true })
            .then(({ total }) => {
              if (load !== loadRef.current || total < 1 || total >= plainTotal) return;
              setPhraseHint({ query: searchQuery, phrase, count: total });
            })
            .catch(() => {
              // Only a hint: without the count it is simply not shown.
            });
        }

        const filtersActive =
          [...Object.keys(apiFilters), ...(resolved.length > 0 ? ['keyword_ids'] : [])].sort().join(',') ||
          undefined;
        // `search` is GA4's recommended event name; `search_term` maps to its built-in dimension.
        trackEvent('search', {
          search_term: searchTerm,
          results_total: response.total,
          page: resultPage,
          filters_active: filtersActive,
          trigger,
        });
        if (response.total === 0) {
          trackEvent('search_no_results', {
            search_term: searchTerm,
            filters_active: filtersActive,
            trigger,
          });
        }
      } catch (err) {
        if (load !== loadRef.current) return;
        console.error('Search error:', err);
        trackEvent('search_error', {
          search_term: searchTerm,
          status: err instanceof ApiServiceError ? err.status : undefined,
          trigger,
        });
        setError(
          err instanceof Error
            ? err.message
            : 'An error occurred while searching. Please try again.'
        );
        setResults(null);
      } finally {
        if (load === loadRef.current) setIsLoading(false);
      }
    },
    [optimizeFilters]
  );

  // Reconcile selected filters against the currently visible facet values.
  // When the auth identity changes (e.g. logout), the backend stops returning
  // previously-visible values (e.g. `source=sunrise` is hidden from anonymous
  // users) and useFacets refetches. Drop any selected value that is no longer
  // present so dangling filter chips disappear, then re-run the active search.
  useEffect(() => {
    if (facets.length === 0) return; // facets not loaded yet — don't wipe selection
    const prev = filtersRef.current;
    let changed = false;
    const next: SearchFilters = {};
    for (const [key, value] of Object.entries(prev)) {
      // Only array-valued (facet) filters are reconciled; date-range string
      // filters and other scalar fields are passed through untouched.
      if (!Array.isArray(value)) {
        next[key] = value;
        continue;
      }
      const facet = facets.find((f) => f.field_name === key);
      if (!facet) {
        // No matching facet field in the visible set — keep as-is to avoid
        // clobbering dynamic fields not represented in the facet response.
        next[key] = value;
        continue;
      }
      const visible = new Set(facet.values.map((v) => v.value));
      const filtered = value.filter((v) => visible.has(v));
      if (filtered.length !== value.length) changed = true;
      if (filtered.length > 0) next[key] = filtered;
    }
    if (!changed) return;
    setFilters(next);
    filtersRef.current = next;
    if (queryRef.current.trim()) {
      setPage(1);
      executeSearch(queryRef.current, next, 0, 'auth', keywordsRef.current);
    }
  }, [facets, executeSearch]);

  // Re-execute search when the content language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      if (queryRef.current.trim()) {
        setPage(1);
        executeSearch(queryRef.current, filtersRef.current, 0, 'language', keywordsRef.current);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, [executeSearch]);

  /**
   * Run a new search using the latest filters, starting at page 1 unless given.
   * The keyword selection is kept unless `selection` replaces it (e.g. from a link).
   */
  const search = useCallback(
    (searchQuery: string, startPage: number = 1, selection?: string[]) => {
      setPage(startPage);
      executeSearch(
        searchQuery,
        filtersRef.current,
        (startPage - 1) * PAGE_SIZE,
        'new',
        selection ?? keywordsRef.current
      );
    },
    [executeSearch]
  );

  /** Show a previously fetched response for a search state without a new request. */
  const restore = useCallback((state: SearchUrlState, response: SearchResponse) => {
    loadRef.current += 1;
    setQuery(state.query);
    queryRef.current = state.query;
    setFilters(state.filters);
    filtersRef.current = state.filters;
    setPage(state.page);
    setActiveSearch(state);
    setResults(response);
    const nav = response.keyword_navigation ?? null;
    setNavigation(nav);
    navigationRef.current = nav;
    const selection = nav ? state.keywords : [];
    setKeywords(selection);
    keywordsRef.current = selection;
    // Only an unnarrowed first page can be reused; anything else is fetched when needed.
    firstPageRef.current = response.from === 0 && selection.length === 0 ? response : null;
    lastResponseRef.current = response;
    setPhraseHint(null);
    setError(null);
    setIsLoading(false);
    setHasSearched(true);
  }, []);

  /** Apply new filters and re-run the active query. */
  const applyFilters = useCallback(
    (newFilters: SearchFilters) => {
      setFilters(newFilters);
      filtersRef.current = newFilters;
      setPage(1);
      if (queryRef.current.trim()) {
        executeSearch(queryRef.current, newFilters, 0, 'filter', keywordsRef.current);
      }
    },
    [executeSearch]
  );

  const goToPage = useCallback(
    (newPage: number) => {
      // Pages belong to the active search, not to unsubmitted edits in the search bar.
      const active = activeSearchRef.current;
      setPage(newPage);
      executeSearch(
        active?.query ?? queryRef.current,
        active?.filters ?? filtersRef.current,
        (newPage - 1) * PAGE_SIZE,
        'page',
        keywordsRef.current
      );
    },
    [executeSearch]
  );

  /** Narrow the active search to a new keyword selection (page 1, no new search). */
  const selectKeywords = useCallback(
    (selection: string[]) => {
      const active = activeSearchRef.current;
      if (!active) return;
      setKeywords(selection);
      keywordsRef.current = selection;
      setPage(1);
      executeSearch(active.query, active.filters, 0, 'topic', selection);
    },
    [executeSearch]
  );

  /** Check or uncheck a keyword in the drawer. */
  const toggleKeyword = useCallback(
    (keywordId: string) => {
      const nav = navigationRef.current;
      if (!nav) return;
      const next = toggled(keywordsRef.current, keywordId);
      trackEvent(next.includes(keywordId) ? 'topic_select' : 'topic_deselect', {
        // Always English, so reports don't split by UI language; the ID survives renames.
        keyword_label: nav.labels_en[keywordId] ?? keywordId,
        keyword_id: keywordId,
        results_total: narrowedDocumentIds(nav, next).length,
      });
      selectKeywords(next);
    },
    [selectKeywords]
  );

  const clearKeywords = useCallback(() => {
    if (keywordsRef.current.length === 0) return;
    trackEvent('topics_clear');
    selectKeywords([]);
  }, [selectKeywords]);

  /** "Clear all filters": the regular filters and the keyword selection. */
  const clearAllFilters = useCallback(() => {
    if (keywordsRef.current.length > 0) trackEvent('topics_clear');
    setKeywords([]);
    keywordsRef.current = [];
    applyFilters({});
  }, [applyFilters]);

  const reset = useCallback(() => {
    loadRef.current += 1;
    setQuery('');
    setFilters({});
    setResults(null);
    setError(null);
    setHasSearched(false);
    setPage(1);
    setActiveSearch(null);
    setNavigation(null);
    setKeywords([]);
    setPhraseHint(null);
    keywordsRef.current = [];
    firstPageRef.current = null;
    lastResponseRef.current = null;
  }, []);

  const totalPages = results ? Math.ceil(results.total / PAGE_SIZE) : 0;

  return {
    query,
    setQuery,
    filters,
    setFilters,
    filtersRef,
    results,
    isLoading,
    error,
    hasSearched,
    page,
    totalPages,
    activeSearch,
    navigation,
    keywords,
    phraseHint,
    search,
    restore,
    applyFilters,
    goToPage,
    toggleKeyword,
    clearKeywords,
    clearAllFilters,
    reset,
  };
}
